import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { shopAxios } from '../services/api.shop';
import { useTranslation } from '../context/TranslationContext';

type Status = 'loading' | 'success' | 'error';

type ConfirmResult =
    | { ok: true; user?: unknown }
    | { ok: false; message: string };

/** Shared across Strict Mode remounts so we only POST once, but every mount can show the result. */
const confirmResults = new Map<string, Promise<ConfirmResult>>();

/** If translation missing, t() often returns the key — prefer a readable fallback. */
function tx(t: (k: string) => string, key: string, fallback: string): string {
    const v = t(key);
    return !v || v === key ? fallback : v;
}

/**
 * Shop-only email confirmation page. Uses /api/auth/confirm-email (ShopScheme cookies).
 * Admin accounts use a completely separate auth stack under /admin.
 */
const ConfirmEmail = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const [status, setStatus] = useState<Status>('loading');
    const [message, setMessage] = useState('');

    useEffect(() => {
        const userId = searchParams.get('userId');
        const token = searchParams.get('token');

        if (!userId || !token) {
            setStatus('error');
            setMessage(tx(t, 'page.confirmEmail.invalidLink', 'This confirmation link is invalid or incomplete.'));
            return;
        }

        const flightKey = `${userId}|${token}`;
        let cancelled = false;

        if (!confirmResults.has(flightKey)) {
            confirmResults.set(
                flightKey,
                (async (): Promise<ConfirmResult> => {
                    try {
                        const { data } = await shopAxios.post('/auth/confirm-email', { userId, token });
                        return { ok: true, user: data?.user };
                    } catch (err: unknown) {
                        let msg = 'Could not confirm your email. The link may be expired. Try logging in if you already confirmed.';
                        if (err && typeof err === 'object' && err !== null && 'response' in err) {
                            const data = (err as { response?: { data?: { message?: string } } }).response?.data;
                            if (data?.message) msg = data.message;
                        }
                        // Allow a later retry (new page load) after failure
                        confirmResults.delete(flightKey);
                        return { ok: false, message: msg };
                    }
                })()
            );
        }

        confirmResults.get(flightKey)!.then((result) => {
            if (cancelled) return;

            if (result.ok) {
                if (result.user) {
                    queryClient.setQueryData(['user'], result.user);
                } else {
                    void queryClient.invalidateQueries({ queryKey: ['user'] });
                }
                setStatus('success');
                setMessage(tx(t, 'page.confirmEmail.success', 'Your email is confirmed. You are now signed in.'));
                setTimeout(() => navigate('/home', { replace: true }), 1500);
            } else {
                setStatus('error');
                setMessage(result.message);
            }
        });

        return () => {
            cancelled = true;
        };
    // Intentionally omit `t` — only used for display strings; re-running on translation
    // refresh must not re-fire confirm / navigate.
    }, [searchParams, navigate, queryClient]);

    return (
        <div className="mx-auto max-w-md px-4 py-10 text-center sm:py-16">
            <h1 className="mb-4 text-2xl font-bold">
                {tx(t, 'page.confirmEmail.title', 'Confirm your email')}
            </h1>

            {status === 'loading' && (
                <div className="flex flex-col items-center gap-4">
                    <div className="h-12 w-12 animate-spin rounded-full border-t-4 border-b-4 border-[var(--accent-color)]" />
                    <p className="text-gray-600">{tx(t, 'page.confirmEmail.working', 'Confirming your email…')}</p>
                </div>
            )}

            {status === 'success' && (
                <div className="rounded-lg bg-green-50 border border-green-200 p-6">
                    <p className="text-green-800 mb-4">{message}</p>
                    <p className="text-sm text-gray-500">
                        {tx(t, 'page.confirmEmail.redirecting', 'Redirecting you to the shop…')}
                    </p>
                </div>
            )}

            {status === 'error' && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-6 space-y-4">
                    <p className="text-red-800">{message}</p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Link
                            to="/login"
                            className="px-4 py-2 rounded bg-[var(--accent-color)] text-white font-medium"
                        >
                            {tx(t, 'page.confirmEmail.goToLogin', 'Go to login')}
                        </Link>
                        <Link to="/register" className="px-4 py-2 rounded border border-gray-300 font-medium">
                            {tx(t, 'page.confirmEmail.registerAgain', 'Register again')}
                        </Link>
                    </div>
                    <p className="text-sm text-gray-600">
                        If this already worked, try{' '}
                        <Link to="/login" className="underline">
                            logging in
                        </Link>
                        .
                    </p>
                </div>
            )}
        </div>
    );
};

export default ConfirmEmail;
